import React, { useState, useEffect } from 'react';
import { X, Image, FileText, Download, Eye, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faImage, faDownload, faEye, faExpand } from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';

const DocumentViewerModal = ({ isOpen, onClose, projectId, projectName }) => {
  const [documents, setDocuments] = useState({ images: [], pdfs: [], documents: [] });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('images');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [pdfZoom, setPdfZoom] = useState(100); // PDF zoom percentage

  // Base URL for document files
  const BASE_DOCUMENT_URL = 'https://umciwms.in/content/document/';

  // Helper function to get full file URL
  const getFullFileUrl = (filePath) => {
    if (!filePath) return '';
    return BASE_DOCUMENT_URL + filePath;
  };

  // Reset zoom and position when image changes
  const resetImageView = () => {
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  };

  // PDF zoom controls
  const handlePdfZoomIn = () => {
    setPdfZoom(prev => Math.min(300, prev + 25));
  };

  const handlePdfZoomOut = () => {
    setPdfZoom(prev => Math.max(50, prev - 25));
  };

  const handlePdfFitToWidth = () => {
    setPdfZoom(100);
  };

  const handlePdfZoomReset = () => {
    setPdfZoom(100);
  };

  useEffect(() => {
    if (isOpen && projectId) {
      loadDocuments();
    }
  }, [isOpen, projectId]);

  // Keyboard shortcuts for image navigation and zoom
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;

      // Image viewer shortcuts (only when image is selected)
      if (selectedImage) {
        switch (event.key) {
          case 'Escape':
            setSelectedImage(null);
            resetImageView();
            break;
          case 'ArrowLeft':
            if (documents.images.length > 1) {
              const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : documents.images.length - 1;
              setCurrentImageIndex(newIndex);
              setSelectedImage(documents.images[newIndex]);
              resetImageView();
            }
            break;
          case 'ArrowRight':
            if (documents.images.length > 1) {
              const newIndex = currentImageIndex < documents.images.length - 1 ? currentImageIndex + 1 : 0;
              setCurrentImageIndex(newIndex);
              setSelectedImage(documents.images[newIndex]);
              resetImageView();
            }
            break;
          case '+':
          case '=':
            event.preventDefault();
            setZoomLevel(prev => Math.min(5, prev + 0.25));
            break;
          case '-':
            event.preventDefault();
            setZoomLevel(prev => Math.max(0.25, prev - 0.25));
            break;
          case '0':
            if (event.ctrlKey || event.metaKey) {
              event.preventDefault();
              setZoomLevel(1);
              setImagePosition({ x: 0, y: 0 });
            }
            break;
          default:
            break;
        }
      }
      
      // PDF viewer shortcuts (only when in PDFs tab and PDF is selected)
      if (activeTab === 'pdfs' && selectedPdf && !selectedImage) {
        switch (event.key) {
          case '+':
          case '=':
            event.preventDefault();
            handlePdfZoomIn();
            break;
          case '-':
            event.preventDefault();
            handlePdfZoomOut();
            break;
          case '0':
            if (event.ctrlKey || event.metaKey) {
              event.preventDefault();
              handlePdfZoomReset();
            }
            break;
          default:
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, selectedPdf, currentImageIndex, documents.images, isOpen, activeTab]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.iwms.getProjectDocuments(projectId);
      setDocuments(response);
      
      // Set default tab based on available content
      if (response.images.length > 0) {
        setActiveTab('images');
      } else if (response.pdfs.length > 0) {
        setActiveTab('pdfs');
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Project Documents</h2>
            <p className="text-sm text-gray-600 mt-1">{projectName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('images')}
            className={`px-6 py-3 font-medium text-sm transition-colors ${
              activeTab === 'images' 
                ? 'border-b-2 border-blue-500 text-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FontAwesomeIcon icon={faImage} className="mr-2" />
            Images ({documents.images.length})
          </button>
          <button
            onClick={() => setActiveTab('pdfs')}
            className={`px-6 py-3 font-medium text-sm transition-colors ${
              activeTab === 'pdfs'
                ? 'border-b-2 border-red-500 text-red-600 bg-red-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FontAwesomeIcon icon={faFilePdf} className="mr-2" />
            PDFs ({documents.pdfs.length})
          </button>
        </div>

        {/* Content Area - Maximize height usage */}
        <div className="flex-1 overflow-hidden" style={{ height: 'calc(90vh - 160px)' }}>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-2 text-gray-600">Loading documents...</span>
            </div>
          ) : (
            <>
              {/* Images Tab */}
              {activeTab === 'images' && (
                <div className="h-full overflow-y-auto">
                  {documents.images.length === 0 ? (
                    <div className="flex items-center justify-center h-64 text-gray-500">
                      <div className="text-center">
                        <FontAwesomeIcon icon={faImage} className="text-4xl mb-4 text-gray-300" />
                        <p>No images uploaded for this project</p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6">
                      {/* Image Gallery Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
                        {documents.images.map((image, index) => (
                          <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                            <div className="aspect-square bg-gray-100 relative overflow-hidden">
                              {/* Actual image display using the full URL */}
                              <img
                                src={getFullFileUrl(image.file_path)}
                                alt={image.file_name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  // Fallback to placeholder if image fails to load
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                                onLoad={(e) => {
                                  // Hide fallback if image loads successfully
                                  if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'none';
                                  }
                                }}
                              />
                              {/* Fallback placeholder */}
                              <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center" style={{ display: 'none' }}>
                                <FontAwesomeIcon icon={faImage} className="text-4xl text-blue-400" />
                              </div>
                              <button
                                onClick={() => {
                                  setSelectedImage(image);
                                  const imageIndex = documents.images.findIndex(img => img.file_name === image.file_name);
                                  setCurrentImageIndex(imageIndex);
                                  resetImageView();
                                }}
                                className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all flex items-center justify-center opacity-0 hover:opacity-100"
                              >
                                <FontAwesomeIcon icon={faExpand} className="text-white text-xl" />
                              </button>
                            </div>
                            <div className="p-3">
                              <h4 className="font-medium text-sm text-gray-800 truncate" title={image.file_name}>
                                {image.file_name}
                              </h4>
                              <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                                <span>{formatFileSize(image.file_size)}</span>
                                <span>{formatDate(image.upload_date)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* PDFs Tab */}
              {activeTab === 'pdfs' && (
                <div className="h-full flex" style={{ height: 'calc(90vh - 200px)' }}>
                  {documents.pdfs.length === 0 ? (
                    <div className="flex items-center justify-center w-full h-64 text-gray-500">
                      <div className="text-center">
                        <FontAwesomeIcon icon={faFilePdf} className="text-4xl mb-4 text-gray-300" />
                        <p>No PDF documents uploaded for this project</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* PDF List Sidebar - Reduced width to give more space to viewer */}
                      <div className="w-1/4 border-r border-gray-200 bg-gray-50 overflow-y-auto">
                        <div className="p-4">
                          <h3 className="font-medium text-gray-800 mb-4">PDF Documents</h3>
                          <div className="space-y-2">
                            {documents.pdfs.map((pdf, index) => (
                              <div
                                key={index}
                                onClick={() => {
                                  setSelectedPdf(pdf);
                                  setPdfZoom(100); // Reset zoom when selecting a new PDF
                                }}
                                className={`p-3 rounded-lg cursor-pointer transition-colors ${
                                  selectedPdf?.file_name === pdf.file_name
                                    ? 'bg-red-100 border border-red-200'
                                    : 'bg-white border border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <div className="flex items-center space-x-3">
                                  <FontAwesomeIcon icon={faFilePdf} className="text-red-500 text-lg" />
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium text-sm text-gray-800 truncate" title={pdf.file_name}>
                                      {pdf.file_name}
                                    </h4>
                                    <div className="flex justify-between items-center mt-1 text-xs text-gray-500">
                                      <span>{formatFileSize(pdf.file_size)}</span>
                                      <span>{formatDate(pdf.upload_date)}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* PDF Viewer - Maximized to use full marked area height */}
                      <div className="flex-1 bg-gray-100 flex flex-col">
                        {selectedPdf ? (
                          <div className="h-full flex flex-col">
                            <div className="p-3 bg-white border-b border-gray-200 flex-shrink-0">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium text-gray-800 text-sm truncate flex-1 mr-4">{selectedPdf.file_name}</h4>
                                <button
                                  onClick={() => window.open(getFullFileUrl(selectedPdf.file_path), '_blank')}
                                  className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 transition-colors flex-shrink-0"
                                >
                                  <FontAwesomeIcon icon={faDownload} className="mr-1" />
                                  Download
                                </button>
                              </div>
                              
                              {/* PDF Zoom Controls */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs text-gray-600">Zoom:</span>
                                  <div className="flex items-center space-x-1 bg-gray-100 rounded px-2 py-1">
                                    <button
                                      onClick={handlePdfZoomOut}
                                      disabled={pdfZoom <= 50}
                                      className="text-gray-600 hover:text-gray-800 disabled:text-gray-400 p-1"
                                      title="Zoom Out"
                                    >
                                      <ZoomOut className="h-3 w-3" />
                                    </button>
                                    <span className="text-xs font-medium min-w-12 text-center text-gray-700">
                                      {pdfZoom}%
                                    </span>
                                    <button
                                      onClick={handlePdfZoomIn}
                                      disabled={pdfZoom >= 300}
                                      className="text-gray-600 hover:text-gray-800 disabled:text-gray-400 p-1"
                                      title="Zoom In"
                                    >
                                      <ZoomIn className="h-3 w-3" />
                                    </button>
                                  </div>
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={handlePdfFitToWidth}
                                    className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 transition-colors"
                                    title="Fit to Width"
                                  >
                                    Fit Width
                                  </button>
                                  <button
                                    onClick={handlePdfZoomReset}
                                    className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200 transition-colors"
                                    title="Reset Zoom"
                                  >
                                    Reset
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div className="flex-1 p-2 min-h-0">
                              <div className="w-full h-full bg-white border border-gray-300 rounded-lg overflow-auto">
                                <div 
                                  className="w-full h-full flex justify-center items-start"
                                  style={{ 
                                    transform: `scale(${pdfZoom / 100})`,
                                    transformOrigin: pdfZoom > 100 ? 'top center' : 'center',
                                    transition: 'transform 0.3s ease-in-out'
                                  }}
                                >
                                  <iframe
                                    src={getFullFileUrl(selectedPdf.file_path)}
                                    className="border-none"
                                    style={{ 
                                      width: `${100 / (pdfZoom / 100)}%`,
                                      height: 'calc(90vh - 280px)',
                                      minHeight: 'calc(90vh - 280px)'
                                    }}
                                    title={selectedPdf.file_name}
                                    onError={() => {
                                      console.error('Failed to load PDF:', selectedPdf.file_path);
                                    }}
                                  >
                                    <div className="flex items-center justify-center h-full text-gray-500">
                                      <div className="text-center">
                                        <FontAwesomeIcon icon={faFilePdf} className="text-6xl mb-4 text-red-300" />
                                        <p className="text-lg font-medium">Unable to load PDF</p>
                                        <p className="text-sm mt-2">
                                          <a 
                                            href={getFullFileUrl(selectedPdf.file_path)} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-blue-500 hover:text-blue-700 underline"
                                          >
                                            Click here to open in new tab
                                          </a>
                                        </p>
                                      </div>
                                    </div>
                                  </iframe>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="h-full flex items-center justify-center text-gray-500">
                            <div className="text-center">
                              <FontAwesomeIcon icon={faEye} className="text-4xl mb-4 text-gray-300" />
                              <p>Select a PDF to view</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center text-sm text-gray-600">
            <span>Project ID: {projectId}</span>
            <span>Total: {documents.documents.length} documents</span>
          </div>
        </div>
      </div>

      {/* Enhanced Image Lightbox with Navigation and Zoom */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-95 z-60 flex items-center justify-center p-4">
          <div className="relative w-full h-full max-w-7xl max-h-full">
            
            {/* Top Controls Bar */}
            <div className="absolute top-4 left-4 right-4 flex justify-between items-center z-10">
              <div className="text-white text-sm bg-black bg-opacity-50 px-3 py-2 rounded">
                Image {currentImageIndex + 1} of {documents.images.length}
              </div>
              <div className="flex items-center space-x-2">
                {/* Zoom Controls */}
                <div className="flex items-center space-x-1 bg-black bg-opacity-50 rounded px-2 py-1">
                  <button
                    onClick={() => setZoomLevel(prev => Math.max(0.25, prev - 0.25))}
                    disabled={zoomLevel <= 0.25}
                    className="text-white hover:text-gray-300 disabled:text-gray-600 p-1"
                    title="Zoom Out"
                  >
                    <ZoomOut className="h-5 w-5" />
                  </button>
                  <span className="text-white text-sm min-w-12 text-center">
                    {Math.round(zoomLevel * 100)}%
                  </span>
                  <button
                    onClick={() => setZoomLevel(prev => Math.min(5, prev + 0.25))}
                    disabled={zoomLevel >= 5}
                    className="text-white hover:text-gray-300 disabled:text-gray-600 p-1"
                    title="Zoom In"
                  >
                    <ZoomIn className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => {
                      setZoomLevel(1);
                      setImagePosition({ x: 0, y: 0 });
                    }}
                    className="text-white hover:text-gray-300 p-1 ml-2 text-xs"
                    title="Reset Zoom"
                  >
                    1:1
                  </button>
                </div>
                
                {/* Close Button */}
                <button
                  onClick={() => {
                    setSelectedImage(null);
                    resetImageView();
                  }}
                  className="text-white hover:text-gray-300 bg-black bg-opacity-50 rounded p-2"
                  title="Close"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Navigation Arrows */}
            {documents.images.length > 1 && (
              <>
                {/* Previous Button */}
                <button
                  onClick={() => {
                    const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : documents.images.length - 1;
                    setCurrentImageIndex(newIndex);
                    setSelectedImage(documents.images[newIndex]);
                    resetImageView();
                  }}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 z-10"
                  title="Previous Image"
                >
                  <ChevronLeft className="h-8 w-8" />
                </button>

                {/* Next Button */}
                <button
                  onClick={() => {
                    const newIndex = currentImageIndex < documents.images.length - 1 ? currentImageIndex + 1 : 0;
                    setCurrentImageIndex(newIndex);
                    setSelectedImage(documents.images[newIndex]);
                    resetImageView();
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 z-10"
                  title="Next Image"
                >
                  <ChevronRight className="h-8 w-8" />
                </button>
              </>
            )}

            {/* Image Container */}
            <div 
              className="flex items-center justify-center w-full h-full overflow-hidden cursor-move"
              onMouseDown={(e) => {
                if (zoomLevel > 1) {
                  setIsDragging(true);
                  setDragStart({
                    x: e.clientX - imagePosition.x,
                    y: e.clientY - imagePosition.y
                  });
                }
              }}
              onMouseMove={(e) => {
                if (isDragging && zoomLevel > 1) {
                  setImagePosition({
                    x: e.clientX - dragStart.x,
                    y: e.clientY - dragStart.y
                  });
                }
              }}
              onMouseUp={() => setIsDragging(false)}
              onMouseLeave={() => setIsDragging(false)}
              onWheel={(e) => {
                e.preventDefault();
                const delta = e.deltaY > 0 ? -0.1 : 0.1;
                setZoomLevel(prev => Math.max(0.25, Math.min(5, prev + delta)));
              }}
            >
              <img
                src={getFullFileUrl(selectedImage.file_path)}
                alt={selectedImage.file_name}
                className="max-w-none transition-transform duration-200 select-none"
                style={{
                  transform: `scale(${zoomLevel}) translate(${imagePosition.x / zoomLevel}px, ${imagePosition.y / zoomLevel}px)`,
                  cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                }}
                onError={(e) => {
                  console.error('Failed to load image:', selectedImage.file_path);
                }}
                onLoad={() => {
                  // Reset position when new image loads
                  setImagePosition({ x: 0, y: 0 });
                }}
                draggable={false}
              />
            </div>

            {/* Bottom Info Bar */}
            <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center">
              <div className="text-white text-sm bg-black bg-opacity-50 px-3 py-2 rounded">
                <div className="font-medium truncate max-w-md" title={selectedImage.file_name}>
                  {selectedImage.file_name}
                </div>
                <div className="text-xs text-gray-300 mt-1">
                  Size: {formatFileSize(selectedImage.file_size)} | 
                  Uploaded: {formatDate(selectedImage.upload_date)}
                </div>
              </div>
              
              {/* Image Thumbnails Navigation */}
              {documents.images.length > 1 && (
                <div className="flex items-center space-x-2 bg-black bg-opacity-50 px-3 py-2 rounded max-w-md overflow-x-auto">
                  {documents.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentImageIndex(index);
                        setSelectedImage(img);
                        resetImageView();
                      }}
                      className={`w-12 h-12 rounded border-2 overflow-hidden flex-shrink-0 ${
                        index === currentImageIndex ? 'border-white' : 'border-transparent'
                      }`}
                    >
                      <img
                        src={getFullFileUrl(img.file_path)}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/></svg>';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentViewerModal;